export type GeolocationFailureReason = 'unsupported' | 'denied' | 'unavailable' | 'timeout';

export type GeolocationOutcome =
  | { ok: true; lat: number; lng: number }
  | { ok: false; reason: GeolocationFailureReason };

// 15 min covers a planning session — the user's position doesn't change
// meaningfully in that window, and multiple location-aware features
// (Find me, Closest to me, Start from where I am) can share one fix.
const CACHE_TTL_MS = 15 * 60_000;

let cached: { lat: number; lng: number; at: number } | null = null;

export async function getCurrentPosition(): Promise<GeolocationOutcome> {
  if (typeof navigator === 'undefined' || !navigator.geolocation) {
    return { ok: false, reason: 'unsupported' };
  }
  if (cached && Date.now() - cached.at < CACHE_TTL_MS) {
    return { ok: true, lat: cached.lat, lng: cached.lng };
  }
  return new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        cached = { lat: pos.coords.latitude, lng: pos.coords.longitude, at: Date.now() };
        resolve({ ok: true, lat: pos.coords.latitude, lng: pos.coords.longitude });
      },
      (err) => {
        // Don't cache failures — we want to re-prompt if the user fixes settings.
        const reason: GeolocationFailureReason =
          err.code === err.PERMISSION_DENIED
            ? 'denied'
            : err.code === err.TIMEOUT
              ? 'timeout'
              : 'unavailable';
        resolve({ ok: false, reason });
      },
      { timeout: 10_000, maximumAge: CACHE_TTL_MS },
    );
  });
}

